import { ClientType } from "@multiweb/clients/ClientType";
import { IExportImportClient, INode } from "@multiweb/clients/exportImport";
import { IClientFactory } from "@multiweb/clients/IClientFactory";
import {
    IOWAttributeDesciption,
    IOWClassAttributeDescription,
    OWDataType,
} from "@multiweb/types/contracts";
import { Key } from "Global/Base/Utils/EventUtils";
import { ClassListGrid } from "UI/UI.Controls/ClassListGrid";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event } from "../../../../../Global/Base/Events";
import { DeferredStateful, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { TimerUtils } from "../../../../../Global/Base/Utils/TimerUtils";
import { EventsController } from "../../../../../Global/Dom/EventsController";
import { IOWClassDescription } from "../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { TreeClassViewRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/TreeClassViewRow";
import { DBDataSource } from "../../../../UI.Controls/DataSource/DBDataSource";
import { TreeClassGrid } from "../../../../UI.Controls/TreeClassGrid";
import { TreeViewGrid } from "../../../../UI.Controls/TreeViewGrid";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../../Controls/Caption";
import { Control } from "../../Controls/Control";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { ButtonType } from "../Actions/Button";
import { LoaderButton } from "../Actions/LoaderButton";

const LOC_LIST = {
    className: "GUI-354669", // Název třídy
    attributeName: "GUI-302180", // Název atributu
    type: "GUI-770996", // Typ
    required: "GUI-459684", // Povinný
    readOnly: "GUI-304082", // Jen pro čtení
};

/**
 * Frame obstarávájící zobrazení podrobností vybrané třídy
 */
export class ExportPanel extends Control {
    private static _idPrefixCount: number = 0;
    protected _grid: TreeClassGrid;
    protected _mainGrid: ClassListGrid;
    private _condition: HTMLTextAreaElement;
    private _conditionSection: HTMLElement;
    private _infoSection: HTMLElement;
    private _attributeInfoSection: HTMLElement;
    private _exportButton: LoaderButton;
    private _eventController: EventsController;
    private _conditionCheckerPromise: DeferredStateful<void>;

    private __client: IExportImportClient | null = null;

    public readonly dataExported: Event<Control, string> = new Event();

    protected _dataSource: DBDataSource;
    protected _idPrefix: string;
    private _disposed = false;

    /**
     * Klient pro zpracování požadavků na službu
     * @private
     */
    private get _client(): IExportImportClient {
        if (!this.__client) this.__client = DIContainer.instance.create(IClientFactory).get(ClientType.exportImport);

        return this.__client;
    }

    /** Čeká na vyhodnocení podmínky */
    private get _conditionAwaiter(): Promise<void> {
        if (this._conditionCheckerPromise) return this._conditionCheckerPromise.promise;

        return Promise.resolve();
    }

    constructor(params: FormControlWithLoggerParams) {
        super(params);
        const { element } = params;
        this._idPrefix = `${ExportPanel._idPrefixCount++}_export_panel_`;

        this.element.classList.add("export-panel");

        this._createSubElements(element);
        this._registerListeners();

        this._mainGrid.loadClasses();
        this._mainGrid.addEventListener("change", async () => {
            await this._grid.loadClass(this._mainGrid.selectedClassNames);
        });

        Caption.createCaptionsForElements(this.element);
    }

    private _getId(id: string): string {
        return `${this._idPrefix}${id}`;
    }

    private _registerListeners(): void {
        if (!this._eventController) this._eventController = new EventsController();

        this._eventController.addListener(this._grid, "SelectionChanged", (e) => this._selectionChanged(e as CustomEvent));
        this._eventController.addListener(this._condition, "change", async () => {
            UIManager.instance.showLoader();

            try {
                this._condition.classList.remove("invalid");
                if (this._conditionCheckerPromise?.isResolved ?? true) this._conditionCheckerPromise = PromiseUtils.deferStateful<void>();

                await TimerUtils.waitOrCancel("importexport", async () => {
                    await this._checkCondition();
                    this._conditionCheckerPromise.resolve();
                }, 500);
            } finally {
                UIManager.instance.hideLoader();
            }
        });

        // Kliknutí na tlačítko Exportu
        const handleClickListener = async () => {
            try {
                await this._conditionAwaiter;
                this._exportButton.enabled = false;
                this._exportButton.isLoading = true;
                const dataForExport = await this.getExportData();

                if (dataForExport.length === 0) {
                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-771088")); // Nelze exportovat prázdná data
                    return;
                }

                await this._client.export(dataForExport);

                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-318366")); // Operace byla spuštěna na pozadí. O výsledku budete informování.
                this.dataExported.trigger(this, await this.getJsonData());
            } catch (e) {
                if (e.message === "Condition is not valid") {
                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-767820")); // Podmínka je nevalidní
                    return;
                }

                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-769937")); // Operace byla přerušena
            } finally {
                this._exportButton.isLoading = false;
                this._exportButton.enabled = true;
            }
        };

        this._eventController.addListener(this._exportButton.element, "click", handleClickListener, { options: { capture: true } });
        this._eventController.addListener(this._exportButton.element, "keydown", (event: KeyboardEvent) => {
            if (event.key === Key.Space) {
                handleClickListener();
            }
        });
    }

    /**
     * Provede kontrolu validnosti zadané podmínky
     * @private
     */
    private async _checkCondition(): Promise<void> {
        const selectedRow = this._grid.firstSelectedRow;
        if (!selectedRow) return;

        try {
            UIManager.instance.showLoader();
            const conditionCheckPromise = PromiseUtils.defer<boolean>();
            const condition = this._condition.value;
            this._condition.disabled = true;
            selectedRow.isConditionValid = conditionCheckPromise.promise;
            selectedRow.condition = condition;

            const result = await OWManager.validateCondition(condition, selectedRow.dataKey, {});

            if (!result.isValid) {
                conditionCheckPromise.resolve(false);
                this._condition.classList.add("invalid");
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-767820")); // Podmínka je nevalidní
            } else {
                conditionCheckPromise.resolve(true);
            }
        } finally {
            this._condition.disabled = false;
            UIManager.instance.hideLoader();
        }
    }

    private _createSubElements(element: HTMLElement): void {
        const createGridComponent = (element: HTMLElement): HTMLElement => {
            const gridBox = createElement({ classes: "grid-box GroupBoxContainer" });
            gridBox.appendChild(element);

            return gridBox;
        };

        const container = createElement({ classes: "frame-container" });
        element.classList.add("group-box--card");
        this._grid = new TreeClassGrid();
        this._mainGrid = new ClassListGrid();
        this._createAttrInfoSection();

        element.appendChild(container);
        container.appendChild(createGridComponent(this._mainGrid));
        container.appendChild(createGridComponent(this._grid));
        container.appendChild(createGridComponent(this._infoSection));
    }

    /**
     * Vytvoří sekci obsahující informace o vybrané položce
     * @private
     */
    private _createAttrInfoSection(): void {
        this._infoSection = createElement({ classes: "attr-info" });
        this._attributeInfoSection = createElement({ classes: "info-section", css: { display: "none" } });
        const buttonSection = createElement({ classes: "button-section" });
        this._exportButton = new LoaderButton("GUI-781368", ButtonType.raised, false); // Spustit export
        this._exportButton.element.tabIndex = 0;

        this._condition = createElement({ type: "textarea", attrs: { "data-captionLocKey": "GUI-321776" } }) as HTMLTextAreaElement; // Podmínka
        this._conditionSection = createElement({ classes: "condition-section", css: { display: "none" } });

        this._conditionSection.appendChild(this._condition);

        this._createAttrDetailInfoSection();

        buttonSection.appendChild(this._exportButton.element);
        this._infoSection.appendChild(buttonSection);
        this._infoSection.appendChild(this._attributeInfoSection);
        this._infoSection.appendChild(this._conditionSection);
    }

    private _createAttrDetailInfoSection(): void {
        const getLine = (locKey: string, id: string | null, value: HTMLElement | null = null) => {
            const el = createElement({ classes: "name" });
            const elValue = createElement({ classes: "value" });

            Localization.addLocData(el, locKey);
            if (value instanceof HTMLElement) elValue.appendChild(value);
            if (id) elValue.id = this._getId(id);

            el.style.display = "none";
            elValue.style.display = "none";

            this._attributeInfoSection.appendChild(el);
            this._attributeInfoSection.appendChild(elValue);
        };
        const getCheckbox = () => {
            const divChb = createElement({ classes: "CustomControl CheckBox CheckBox2s Disabled" });
            const input = createElement({ type: "input[checkbox]", value: false }) as HTMLInputElement;
            input.readOnly = true;
            divChb.appendChild(input);
            divChb.appendChild(document.createElement("label"));
            return divChb;
        };

        const locKeyList = Object.entries(LOC_LIST);

        for (const [key, value] of locKeyList) {
            if (key == "readOnly" || key == "required") getLine(value, key, getCheckbox());
            else getLine(value, key);
        }
    }

    private _selectionChanged(e: CustomEvent): void {
        const selectedData = e.detail as { dataGrid: TreeViewGrid; rowID: string | undefined; data: IOWAttributeDesciption | IOWClassDescription; row: TreeClassViewRow };
        const keys = Object.keys(LOC_LIST).map(i => this._getId(i));

        for (const key of keys) {
            document.getElementById(key).style.display = "none";
            (document.getElementById(key).previousElementSibling as HTMLElement).style.display = "none";
        }

        const setLine = (id: string, value: string | boolean) => {
            const el = document.getElementById(this._getId(id));

            el.style.display = "";
            (el.previousElementSibling as HTMLElement).style.display = "";

            if (typeof value === "boolean" || el.childElementCount > 0) el.querySelector("input").value = "" + (value ?? false);
            else el.textContent = value;
        };

        this._attributeInfoSection.style.display = "";

        if (selectedData.rowID && !("attributeName" in selectedData.data)) {
            const className = (selectedData.data as IOWClassDescription).className;
            setLine("className", className);

            this._conditionSection.style.display = "block";
        } else {
            const attrInfo = selectedData.data as IOWAttributeDesciption;

            if (attrInfo.type === OWDataType.OWObject) {
                this._conditionSection.style.display = "block";

                const classAttrInfo = (selectedData.data as IOWClassAttributeDescription);
                setLine("className", classAttrInfo.className);
            } else {
                this._conditionSection.style.display = "none";
            }

            setLine("attributeName", attrInfo.attributeName);
            setLine("type", attrInfo.type);
            setLine("required", attrInfo.required);
            setLine("readOnly", attrInfo.readOnly);
        }

        this._condition.value = selectedData.row.condition;
        this._condition.classList.toggle("invalid", !selectedData.row.isConditionValid);
    }

    /**
     * Vrátí strom tříd/property, které byly vybrány k exportu
     */
    public getExportData(): Promise<readonly INode[]> {
        return this._grid.getObjectData();
    }

    /** Vrátí strom tříd/property jako JSON */
    public async getJsonData(): Promise<string> {
        return JSON.stringify(await this.getExportData(), undefined, 4);
    }

    public override dispose(): void {
        if (this._disposed) {
            return;
        }
        super.dispose();

        this._eventController.removeAllListeners();

        this._grid.dispose();
        this._mainGrid.dispose();

        this._disposed = true;
    }
}
